export class TimeSpan {
  days = 0;
  hours = 0;
  minutes = 0;
  seconds = 0;
  isNullable = false;

  constructor(value = '', isNullable = false) {
    this.isNullable = isNullable;

    if (value) {
      const days = value.split('.');
      if (days.length > 1) {
        this.days = +days[0];
        value = days[1];
      }
      const time = value.split(':');
      this.hours = +time[0];
      this.minutes = +time[1];
      if (time.length > 2) {
        this.seconds = +time[2];
      }
    }
  }

  set(days = null, hours = null, minutes = null, seconds = null) {
    this.days = days ?? this.days;
    this.hours = hours ?? this.hours;
    this.minutes = minutes ?? this.minutes;
    this.seconds = seconds ?? this.seconds;
  }

  toString(showDays = true) {
    if (this.isNullable && this.totalSeconds() === 0) {
      return null;
    }

    let days = '';
    let hours = this.hours;

    if (showDays && this.days) {
      days = this.days + '.';
    } else if (!showDays && this.days) {
      hours += this.days * 24; // convert days into hours
    }

    const hour = hours < 10 ? '0' + hours : hours;
    const min = this.minutes < 10 ? '0' + this.minutes : this.minutes;

    let seconds = '';
    if (this.seconds) {
      const sec = this.seconds < 10 ? '0' + this.seconds : this.seconds;
      seconds = ':' + sec;
    }

    return days + hour + ':' + min + seconds;
  }

  toDisplayString(showDays = true): string {
    const result = this.toString(showDays);
    if (!result) {
      return '';
    }
    return result.replace('.', ' ');
  }

  add(other: TimeSpan): TimeSpan {
    const totalSeconds = this.totalSeconds() + other.totalSeconds();
    return TimeSpan.fromTotalSeconds(totalSeconds);
  }

  subtract(other: TimeSpan): TimeSpan {
    const totalSeconds = this.totalSeconds() - other.totalSeconds();
    return TimeSpan.fromTotalSeconds(totalSeconds);
  }

  equals(other: TimeSpan): boolean {
    return this.totalSeconds() === other.totalSeconds();
  }

  totalHours(): number {
    return this.days * 24 + this.hours + this.minutes / 60 + this.seconds / 3600;
  }

  totalMinutes(): number {
    return (this.days * 24 + this.hours) * 60 + this.minutes;
  }

  totalSeconds(): number {
    return ((this.days * 24 + this.hours) * 60 + this.minutes) * 60 + this.seconds;
  }

  static fromTotalSeconds(total: number, isNullable = false): TimeSpan {
    const span = new TimeSpan(null, isNullable);
    const days = Math.floor(total / 86400);
    total %= 86400;
    const hours = Math.floor(total / 3600);
    total %= 3600;
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    span.set(days, hours, minutes, seconds);

    return span;
  }

  static create(
    days: number = 0,
    hours: number = 0,
    minutes: number = 0,
    seconds: number = 0,
    isNullable = false
  ): TimeSpan {
    const span = new TimeSpan(null, isNullable);
    span.set(days, hours, minutes, seconds);
    return span;
  }

  static isValid(value: unknown): value is string {
    if (value === null || value === undefined) {
      return false;
    }

    const text = value.toString().trim();
    if (!text) {
      return false;
    }

    const dayParts = text.split('.');
    if (dayParts.length > 2) {
      return false;
    }

    const timeText = dayParts.length === 2 ? dayParts[1] : dayParts[0];
    if (dayParts.length === 2 && !TimeSpan.isDigits(dayParts[0])) {
      return false;
    }

    const timeParts = timeText.split(':');
    if (timeParts.length < 2 || timeParts.length > 3) {
      return false;
    }

    const [hours, minutes, seconds] = timeParts;
    return (
      TimeSpan.hasDigitLength(hours, 1, 2) &&
      TimeSpan.hasDigitLength(minutes, 2, 2) &&
      (seconds === undefined || TimeSpan.hasDigitLength(seconds, 2, 2))
    );
  }

  static getTotalHours(
    startsAt?: TimeSpan | string,
    endsAt?: TimeSpan | string,
    excludeFrom?: TimeSpan | string,
    excludeTo?: TimeSpan | string
  ): TimeSpan {
    if (!startsAt || !endsAt) {
      return TimeSpan.create();
    }

    const startSpan = startsAt instanceof TimeSpan ? startsAt : new TimeSpan(startsAt);
    const endSpan = endsAt instanceof TimeSpan ? endsAt : new TimeSpan(endsAt);

    let result = TimeSpan.create(0, endSpan.totalSeconds() < startSpan.totalSeconds() ? 24 : 0)
      .add(endSpan)
      .subtract(startSpan);

    if (result.totalSeconds() > 0 && excludeFrom && excludeTo) {
      const excludeFromSpan = excludeFrom instanceof TimeSpan ? excludeFrom : new TimeSpan(excludeFrom);
      const excludeToSpan = excludeTo instanceof TimeSpan ? excludeTo : new TimeSpan(excludeTo);
      const exclude = TimeSpan.create(0, excludeToSpan.totalSeconds() < excludeFromSpan.totalSeconds() ? 24 : 0)
        .add(excludeToSpan)
        .subtract(excludeFromSpan);
      result = result.subtract(exclude);
    }

    return result;
  }

  private static hasDigitLength(value: string | undefined, min: number, max: number): boolean {
    return !!value && value.length >= min && value.length <= max && TimeSpan.isDigits(value);
  }

  private static isDigits(value: string): boolean {
    return [...value].every((char) => char >= '0' && char <= '9');
  }
}
